import { Button, Flex, Form, FormProps, Typography } from 'antd'
import React, { ReactNode, useEffect, useState } from 'react'
import { DesignSystem } from '~/core/designSystem'
import { ButtonPrimary } from '~/features/shared/components'
import { Icon } from '~/features/shared/icons'
import { OnboardingStepProps } from '../types'

type Props = {
  title?: string
  description?: string
  children: ReactNode
} & OnboardingStepProps

export const Step: React.FC<Props> = ({
  children,
  isSubmit,
  title,
  description,
  values,
  onSave,
  onNext,
  onBack,
  canNext,
  canBack,
  onChange,
}) => {
  const designSystem = DesignSystem.use()

  const isMobile = designSystem.isMobile

  const [isLoading, setLoading] = useState(false)
  const [form] = Form.useForm()

  useEffect(() => {
    form.setFieldsValue(values)
  }, [form, values])

  const submit = async (valuesUpdated: any) => {
    if (onSave) {
      await onSave(valuesUpdated)
    }

    return valuesUpdated
  }

  const handleValuesChange: FormProps['onValuesChange'] = (
    _,
    valuesUpdated,
  ) => {
    onChange?.({ ...values, ...valuesUpdated })
  }

  const handleNext: FormProps['onFinish'] = async () => {
    setLoading(true)

    const valuesUpdated = form.getFieldsValue(true)

    await submit(valuesUpdated)

    await onNext(valuesUpdated)

    setLoading(false)
  }

  const handleBack = async () => {
    const valuesUpdated = form.getFieldsValue(true)

    await onBack(valuesUpdated)
  }

  return (
    <>
      <Flex vertical className="w-full h-full" gap={16}>
        <Flex vertical className="w-full">
          {title && <Typography.Title level={2}>{title}</Typography.Title>}
          {description && (
            <Typography.Text type="secondary">{description}</Typography.Text>
          )}
        </Flex>

        <Form
          form={form}
          layout="vertical"
          scrollToFirstError
          requiredMark={false}
          onFinish={handleNext}
          onValuesChange={handleValuesChange}
          className="w-full"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Flex vertical className="w-full" flex={1} justify="center">
            {children}

            {isMobile && (
              <div style={{ height: '250px' }}>
                <span />
              </div>
            )}
          </Flex>

          <Flex
            justify="space-between"
            style={{
              height: '40px',
              position: isMobile ? 'fixed' : undefined,
              bottom: isMobile ? '100px' : undefined,
              left: isMobile ? 16 : undefined,
              right: isMobile ? 16 : undefined,
            }}
          >
            <Flex flex={1}>
              <Form.Item shouldUpdate={true}>
                {instance => {
                  const valuesNow = instance.getFieldsValue(true)

                  if (!onBack || (canBack && !canBack(valuesNow, form))) {
                    return <span />
                  }

                  return (
                    <Button
                      icon={<Icon name="chevron-left" />}
                      size="large"
                      type="text"
                      onClick={handleBack}
                    >
                      <Typography.Text type="secondary"> Back</Typography.Text>
                    </Button>
                  )
                }}
              </Form.Item>
            </Flex>

            <Flex flex={1} justify="right">
              <Form.Item shouldUpdate={true}>
                {instance => {
                  const valuesNow = instance.getFieldsValue(true)

                  if (!onNext || (canNext && !canNext(valuesNow, form))) {
                    return <span />
                  }

                  return (
                    <ButtonPrimary
                      icon={<Icon name="arrow-long-right" />}
                      iconPosition="end"
                      size="large"
                      htmlType="submit"
                      loading={isLoading}
                    >
                      {isSubmit ? 'Submit 🎉' : 'Next'}
                    </ButtonPrimary>
                  )
                }}
              </Form.Item>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    </>
  )
}
